<template lang="pug">
  v-card
    v-card-title
      h2.card-title {{ 'base.filter' | translate }}
    v-card-content
      .cols-x
        .cols-x__2
          v-date-field(
            :label="$t('reports.date_from')"
            v-model="form.planned_on_after")
        .cols-x__2
          v-date-field(
            :label="$t('reports.date_to')"
            v-model="form.planned_on_before")
      .cols-x
        .cols-x__2.mb-3
          v-multiselect(
            v-if="allowViewAnimalTypes"
            item-value="id"
            :label="$t('reports.animal_type')"
            :options="animalTypesList"
            v-model="form.animal_type")
        .cols-x__2.mb-3
          v-multiselect(
            v-if="allowViewGroups"
            item-value="id"
            :label="$t('reports.animal_groups')"
            :options="groupsList"
            v-model="form.group")
      .cols-x(v-if="isConsumptionPath")
        .cols-x__2.mb-3
          v-multiselect(
            v-if="allowViewRations"
            item-value="id"
            :label="$t('reports.rations')"
            :options="rationsList"
            v-model="form.ration")
        .cols-x__2.mb-3
          v-multiselect(
            v-if="allowViewEmployees"
            item-value="id"
            :label="$t('reports.employees')"
            :options="filteredUsers"
            v-model="form.worker")
      .cols-x(v-if="isConsumptionPath")
        .cols-x__2.mb-3.remove-margin-above-mobile
          v-multiselect(
            v-if="allowViewLoadings"
            item-value="id"
            :label="'base.periods' | translate"
            :options="loadings"
            v-model="form.loading")
        .cols-x__2
          v-multiselect(
            item-value="key"
            :label="$t('reports.statuses')"
            :options="statuses"
            :use-primitive-value="true"
            v-model="form.status")
    v-card-actions
      v-btn.mr-2(
        :loading="loading"
        @click="onApply") {{ 'base.apply' | translate }}
      v-btn(
        text
        @click="onReset")
        | {{ 'base.clear' | translate }}
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'

import { getDateForDatePicker } from '@/util'

export default {
  name: 'FiltersModal',

  data: () => ({
    form: {
      planned_on_before: null,
      planned_on_after: null,
      animal_type: [],
      group: [],
      ration: [],
      worker: [],
      loading: [],
      status: []
    },
    loadings: [],
    statuses: [
      { id: 0, name: 'status.waited', key: 'waited' },
      { id: 1, name: 'status.in_process', key: 'in_process' },
      { id: 2, name: 'status.loaded', key: 'loaded' },
      { id: 3, name: 'status.uploaded', key: 'uploaded' },
      { id: 4, name: 'status.out_process', key: 'out_process' },
    ],
    loading: false
  }),

  computed: {
    isConsumptionPath () {
      return !this.$route.path.includes('consumption')
    },
    ...mapGetters('reports', [
      'filters'
    ]),

    ...mapGetters('animaltypes', [
      'animalTypesList'
    ]),

    ...mapGetters('groups', [
      'groupsList'
    ]),

    ...mapGetters('employees', [
      'employees'
    ]),

    ...mapGetters('rations', [
      'rationsList'
    ]),
    timezone () {
      return this.$store.getters['account/timezone']
    },

    filteredUsers () {
      return this.employees.map(user => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        groups: user.groups
      }))
    },

    allowViewAnimalTypes () {
      return this.hasPermission('animal_type', 'view')
    },

    allowViewGroups () {
      return this.hasPermission('group', 'view')
    },

    allowViewRations () {
      return this.hasPermission('ration', 'view')
    },

    allowViewEmployees () {
      return this.$store.getters.userType === 'client' ||
        this.$store.getters.userType === 'manager'
    },

    allowViewLoadings () {
      return this.hasPermission('loading', 'view')
    }
  },

  mounted () {
    if (!this.filters.planned_on_before && !this.filters.planned_on_after) {
      const today = getDateForDatePicker(this.timezone)
      this.$store.dispatch('reports/applyFilters', {
        planned_on_before: today,
        planned_on_after: today,
        farm: this.$route.params.farm_id
      })
    }

    for (const key in this.filters) {
      this.form[key] = this.filters[key]
    }
    this.loadData()
  },

  methods: {
    ...mapActions('reports', [
      'applyFilters',
      'resetFilters',
      'resetFiltersChart'
    ]),

    ...mapActions('animaltypes', [
      'loadAnimalTypesList'
    ]),

    ...mapActions('groups', [
      'loadGroupsList'
    ]),

    ...mapActions('employees', [
      'loadEmployees'
    ]),

    ...mapActions('rations', [
      'loadRationsList'
    ]),

    async loadData () {
      const params = {
        farm: this.$route.params.farm_id,
        limit: 1000,
      }
      if (this.allowViewAnimalTypes) {
        await this.loadAnimalTypesList(params)
      }
      if (this.allowViewGroups) {
        await this.loadGroupsList(params)
      }
      if (this.allowViewRations) {
        await this.loadRationsList(params)
      }
      if (this.allowViewEmployees) {
        await this.loadEmployees()
      }
      if (this.allowViewLoadings) {
        const { data } = await api.loadings.list(params)
        this.loadings = data
      }
    },

    onApply () {
      this.loading = true
      const filters = {}
      for (const key in this.form) {
        if (this.form[key] !== null) {
          if (Array.isArray(this.form[key])) {
            if (this.form[key].length) {
              filters[key] = this.form[key]
            }
          } else {
            filters[key] = this.form[key]
          }
        }
      }
      filters.farm = this.$route.params.farm_id
      this.applyFilters(filters)
      this.loading = false
      this.$emit('close')
    },

    onReset () {
      for (const key in this.form) {
        if (Array.isArray(this.form[key])) {
          this.form[key] = []
        } else {
          if (key === 'planned_on_before' || key === 'planned_on_after') {
            this.form[key] = getDateForDatePicker(this.timezone)
          } else {
            this.form[key] = null
          }
        }
      }
      if (!this.isConsumptionPath) {
        this.resetFiltersChart()
      } else {
        this.resetFilters()
      }

      const allowedKeys = ['farm', 'planned_on_before', 'planned_on_after', 'page']
      const newQuery = {}

      for (const key of allowedKeys) {
        if (this.$route.query[key]) {
          newQuery[key] = this.$route.query[key]
        }
      }

      newQuery.page = 1

      this.$router.replace({ query: newQuery })
      this.$emit('close')
    }
  }
}
</script>
